// packages/front/src/portfolio/modals/add-income-event-modal.tsx
//
// Stage 5 (portfolio-income-events) — "Add / Edit Income Event" modal.
//
// Income events track cash distributions from securities (dividends, coupons,
// withholding tax). Separated from transactions (buy/sell) per
// portfolio-income-events plan.
//
// Create mode: useCreateIncomeEvent. Edit mode: useUpdateIncomeEvent (single
// record seeded from initialData).

import {
  CURRENCY_LIST,
  INCOME_EVENT_TYPE,
  INCOME_EVENT_TYPE_LABELS,
  INCOME_EVENT_TYPE_SIGN,
  MONEY_SIGN_OBJ,
  type TIncomeEventType,
} from "@signals/shared";
import { useEffect, useMemo, useState, type FC, type FormEvent } from "react";
import {
  Button,
  Modal,
  Select,
  SymbolCombobox,
  toDateInput,
} from "../../_common";
import { apiErrorMessage } from "../portfolio-api-error";
import { accountUnavailableMessage, operationAccountOptions } from "../portfolio-account-availability";
import {
  useCreateIncomeEvent,
  usePortfolioAccounts,
  useUpdateIncomeEvent,
  type IncomeEvent,
} from "../portfolio.hooks";

export type { IncomeEvent } from "../portfolio.hooks";

export interface AddIncomeEventModalProps {
  open: boolean;
  onClose: () => void;
  portfolioId?: string;
  editId?: string | undefined;
  initialData?: Partial<IncomeEvent> | undefined;
}

const inputClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm";
const numberClass =
  "h-9 rounded-md border border-input bg-background px-3 py-1 text-sm [appearance:textfield]";
const labelClass = "text-sm font-medium";

export const AddIncomeEventModal: FC<AddIncomeEventModalProps> = ({
  open,
  onClose,
  portfolioId,
  editId,
  initialData,
}) => {
  const isEdit = Boolean(editId);
  const accountsQuery = usePortfolioAccounts(portfolioId);
  const accounts = accountsQuery.data?.items ?? [];
  const createIncomeEvent = useCreateIncomeEvent();
  const updateIncomeEvent = useUpdateIncomeEvent();
  const saving = createIncomeEvent.isPending || updateIncomeEvent.isPending;

  const [accountId, setAccountId] = useState("");
  const [symbolId, setSymbolId] = useState("");
  const [symbolLabel, setSymbolLabel] = useState<string | null>(null);
  const [type, setType] = useState<TIncomeEventType>("dividend");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const accountCurrency = useMemo(() => {
    const acc = accounts.find((a) => a.id === accountId);
    return acc?.currency ?? null;
  }, [accounts, accountId]);

  // Seed form when opening.
  useEffect(() => {
    if (!open) return;
    setError(null);
    if (isEdit && initialData) {
      const accCur =
        accounts.find((a) => a.id === (initialData.accountId ?? ""))
          ?.currency ?? "";
      if (initialData.accountId !== undefined)
        setAccountId(initialData.accountId);
      setSymbolId(initialData.symbolId ?? "");
      setSymbolLabel(null);
      setType(initialData.type ?? "dividend");
      setAmount(initialData.amount != null ? String(initialData.amount) : "");
      setCurrency(initialData.currency ?? accCur);
      setDate(toDateInput(initialData.date));
      setNote(initialData.note ?? "");
    } else {
      // Create mode.
      const accCur = initialData?.accountId
        ? (accounts.find((a) => a.id === initialData.accountId)?.currency ?? "")
        : "";
      setAccountId(initialData?.accountId ?? "");
      setSymbolId("");
      setSymbolLabel(null);
      setType("dividend");
      setAmount("");
      setCurrency(accCur);
      setDate(new Date().toISOString().slice(0, 10));
      setNote("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Default currency from account when currency is empty.
  useEffect(() => {
    if (!accountCurrency) return;
    if (!currency) setCurrency(accountCurrency);
  }, [accountCurrency, currency]);

  // A closed account takes operations only up to its closure date; the edited event's own account stays listed.
  const dates = useMemo(() => [date], [date]);
  const accountOptions = useMemo(
    () => operationAccountOptions(accounts, dates, [accountId, initialData?.accountId]),
    [accounts, dates, accountId, initialData?.accountId],
  );
  const accountError = useMemo(
    () => accountUnavailableMessage(accounts.find((a) => a.id === accountId), dates),
    [accounts, accountId, dates],
  );

  const signHint = useMemo(() => {
    const sign = INCOME_EVENT_TYPE_SIGN[type];
    return sign === MONEY_SIGN_OBJ.POSITIVE
      ? "Money in (positive amount)."
      : "Money out (negative amount).";
  }, [type]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!portfolioId) {
      setError("Select a portfolio first");
      return;
    }
    if (!accountId) {
      setError("Select account");
      return;
    }
    if (accountError) {
      setError(accountError);
      return;
    }
    if (!currency) {
      setError("Select currency");
      return;
    }
    if (!symbolId) {
      setError("Symbol is required");
      return;
    }
    const payload = {
      accountId,
      symbolId,
      type,
      amount: Number(amount) || 0,
      currency: currency || "USD",
      date,
      note,
    };
    try {
      if (isEdit && editId) {
        await updateIncomeEvent.mutateAsync({
          id: editId,
          portfolioId,
          body: payload,
        });
      } else {
        await createIncomeEvent.mutateAsync({ portfolioId, body: payload });
      }
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Failed to save income event"));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Income Event" : "Add Income Event"}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        {error && (
          <div className="rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">
            {error}
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className={labelClass}>Account</label>
          <Select
            className={inputClass}
            value={accountId}
            onChange={(value) => {
              setAccountId(value);
              setError(null);
            }}
            placeholder={
              accounts.length === 0
                ? "No accounts yet — create one first"
                : "Select account"
            }
            options={accountOptions}
            ariaLabel="Account"
          />
          {accountError && (
            <p role="alert" className="text-sm text-destructive">
              {accountError}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>Symbol</label>
          <SymbolCombobox
            value={symbolId}
            onChange={(id) => setSymbolId(id)}
            onSelectItem={(item) => {
              if (item.currency && !currency) setCurrency(item.currency);
            }}
            initialLabel={symbolLabel ?? undefined}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>Type</label>
          <Select
            className={inputClass}
            value={type}
            onChange={(value) => {
              const selectedType = INCOME_EVENT_TYPE.find(
                (eventType) => eventType === value,
              );
              if (selectedType) setType(selectedType);
            }}
            options={INCOME_EVENT_TYPE.map((t) => ({
              value: t,
              label: INCOME_EVENT_TYPE_LABELS[t]["en-US"] ?? t,
            }))}
            ariaLabel="Type"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>Amount</label>
          <input
            className={numberClass}
            type="number"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            required
          />
          <p className="text-xs text-muted-foreground">{signHint}</p>
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>Currency</label>
          <Select
            className={inputClass}
            value={currency}
            onChange={(value) => {
              setCurrency(value);
              setError(null);
            }}
            placeholder="Select currency"
            options={CURRENCY_LIST.map((c) => ({ value: c, label: c }))}
            ariaLabel="Currency"
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>Date</label>
          <input
            className={inputClass}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className={labelClass}>Note</label>
          <input
            className={inputClass}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="mt-2 flex gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || accountError !== null}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
